import React, { useState, useMemo, useRef, useEffect } from 'react';
import ktruNames from '../assets/ktru_names.json';
import { KtruSummary } from './KtruDashboard';

const formatPrice = (price) =>
    price != null
        ? new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(price)
        : '—';

const PAGE_SIZE = 20;

const getPaginationPages = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 4) return [1, 2, 3, 4, 5, '...', total];
    if (current >= total - 3) return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
    return [1, '...', current - 1, current, current + 1, '...', total];
};

const KtruExtended = ({ results, ktru }) => {
    const [page, setPage] = useState(1);
    const [sortPrice, setSortPrice] = useState(null); // null | 'asc' | 'desc'
    const [selectedBrands, setSelectedBrands] = useState(null); // null = все
    const [filterOpen, setFilterOpen] = useState(false);
    const filterRef = useRef(null);

    useEffect(() => {
        if (!filterOpen) return;
        const handle = (e) => {
            if (filterRef.current && !filterRef.current.contains(e.target)) {
                setFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handle);
        return () => document.removeEventListener('mousedown', handle);
    }, [filterOpen]);

    const flat = useMemo(() => {
        const arr = [];
        for (const brand of results ?? []) {
            for (const cluster of brand.product_clusters ?? []) {
                for (const contract of cluster.contracts ?? []) {
                    arr.push({
                        folder: contract.folder,
                        name: contract.name,
                        price: contract.price,
                        brand: brand.brands,
                        customer_inn: contract.customer_inn,
                    });
                }
            }
        }
        return arr;
    }, [results]);

    const allBrands = useMemo(() => {
        const set = new Set(flat.map(i => i.brand || 'Без бренда'));
        return [...set].sort();
    }, [flat]);

    const toggleBrand = (brand) => {
        setPage(1);
        setSelectedBrands(prev => {
            const current = prev ?? new Set(allBrands);
            const next = new Set(current);
            if (next.has(brand)) next.delete(brand);
            else next.add(brand);
            return next.size === allBrands.length ? null : next;
        });
    };

    const isAllSelected = selectedBrands === null;

    const toggleAll = () => {
        setPage(1);
        setSelectedBrands(null);
    };

    const filtered = useMemo(() => {
        let arr = selectedBrands === null
            ? flat
            : flat.filter(i => selectedBrands.has(i.brand || 'Без бренда'));

        if (sortPrice === 'asc') arr = [...arr].sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));
        else if (sortPrice === 'desc') arr = [...arr].sort((a, b) => (b.price ?? -Infinity) - (a.price ?? -Infinity));

        return arr;
    }, [flat, selectedBrands, sortPrice]);

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const cycleSortPrice = () => {
        setPage(1);
        setSortPrice(s => s === null ? 'asc' : s === 'asc' ? 'desc' : null);
    };

    const activeBrandCount = selectedBrands === null ? allBrands.length : selectedBrands.size;

    return (
        <div className="ktru-extended">
            <KtruSummary results={results} />
            {/* Панель управления */}
            <div className="ktru-ext-toolbar">
                <div className="ktru-ext-toolbar-left">
                    <button
                        className={`ktru-ext-sort-btn${sortPrice ? ' active' : ''}`}
                        onClick={cycleSortPrice}
                    >
                        Цена
                        {sortPrice === 'asc' && ' ↑'}
                        {sortPrice === 'desc' && ' ↓'}
                        {!sortPrice && ' ↕'}
                    </button>

                    <div className="ktru-ext-filter-wrap" ref={filterRef}>
                        <button
                            className={`ktru-ext-filter-btn${selectedBrands !== null ? ' active' : ''}`}
                            onClick={() => setFilterOpen(o => !o)}
                        >
                            Производители
                            {selectedBrands !== null && (
                                <span className="ktru-ext-filter-badge">{activeBrandCount}</span>
                            )}
                            <span className="ktru-ext-filter-arrow">{filterOpen ? '▲' : '▼'}</span>
                        </button>

                        {filterOpen && (
                            <div className="ktru-ext-filter-dropdown">
                                <div className="ktru-ext-filter-item ktru-ext-filter-all">
                                    <input
                                        type="checkbox"
                                        checked={isAllSelected}
                                        onChange={toggleAll}
                                    />
                                    <span>Все поставщики</span>
                                    <button
                                        className="ktru-ext-clear-btn"
                                        onClick={(e) => { e.stopPropagation(); setPage(1); setSelectedBrands(new Set()); }}
                                        title="Снять все"
                                    >
                                        ×
                                    </button>
                                </div>
                                <div className="ktru-ext-filter-divider" />
                                {allBrands.map(brand => (
                                    <label key={brand} className="ktru-ext-filter-item">
                                        <input
                                            type="checkbox"
                                            checked={selectedBrands === null || selectedBrands.has(brand)}
                                            onChange={() => toggleBrand(brand)}
                                        />
                                        <span>{brand}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <span className="ktru-ext-count">{filtered.length} контрактов</span>
            </div>

            <div className="ktru-extended-list">
                {paged.map((item, idx) => (
                    <div key={idx} className="ktru-ext-card">
                        <div className="ktru-ext-left">
                            {item.folder ? (
                                <a
                                    className="ktru-ext-contract-num"
                                    href={`https://zakupki.gov.ru/epz/contract/search/results.html?searchString=${item.folder}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    № {item.folder}
                                </a>
                            ) : (
                                <span className="ktru-ext-contract-num ktru-ext-no-contract">Без контракта</span>
                            )}
                            <div className="ktru-ext-field">
                                <span className="ktru-ext-label">Наименование</span>
                                <span className="ktru-ext-value">{item.name || '—'}</span>
                            </div>
                        </div>

                        <div className="ktru-ext-right">
                            <div className="ktru-ext-field">
                                <span className="ktru-ext-label">КТРУ</span>
                                <a
                                    className="ktru-ext-value ktru-ext-code"
                                    href={`https://zakupki.gov.ru/epz/ktru/search/results.html?searchString=${ktru}&morphology=on`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {ktru}
                                </a>
                                {ktruNames[ktru] && (
                                    <span className="ktru-ext-ktru-name">({ktruNames[ktru]})</span>
                                )}
                            </div>
                            <div className="ktru-ext-field">
                                <span className="ktru-ext-label">Цена за единицу</span>
                                <span className="ktru-ext-value ktru-ext-price">
                                    {item.price != null ? formatPrice(item.price) : '—'}
                                </span>
                            </div>
                            {item.customer_inn && (
                                <div className="ktru-ext-field">
                                    <span className="ktru-ext-label">ИНН заказчика</span>
                                    <span className="ktru-ext-value">{item.customer_inn}</span>
                                </div>
                            )}
                            {item.brand && (
                                <div className="ktru-ext-field">
                                    <span className="ktru-ext-label">Производитель</span>
                                    <span className="ktru-ext-value">{item.brand}</span>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {totalPages > 1 && (
                <div className="pagination">
                    {getPaginationPages(page, totalPages).map((p, i) =>
                        p === '...'
                            ? <span key={`e-${i}`} className="pagination-ellipsis">...</span>
                            : <button
                                key={p}
                                className={`pagination-btn${p === page ? ' active' : ''}`}
                                onClick={() => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                            >
                                {p}
                            </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default KtruExtended;
